'use client'

import { useId } from 'react'

/**
 * NoiseSvgFilter - Reusable noise SVG filter definitions
 *
 * Used internally by NoiseTexture and navbar components with noiseBlend.
 * Renders the SVG filter definitions and rectangles that produce the noise effect.
 *
 * @internal - Use NoiseTexture for page-level noise, or noiseBlend prop on Navbar
 */
export function NoiseSvgFilter({ className, id: stableId }: { className?: string; id?: string }) {
  const reactId = useId()
  const filterId = stableId ? `noise-${stableId}` : `noise-${reactId}`
  const scale = 0.8

  return (
    <>
      {/* Light mode - SVG noise with dark specks, intensity 0.05 */}
      <svg
        className={`absolute inset-0 h-full w-full opacity-[0.05] dark:hidden ${className ?? ''}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter id={`${filterId}-light`} x="0%" y="0%" width="100%" height="100%">
            <feTurbulence
              type="fractalNoise"
              baseFrequency={scale}
              numOctaves={4}
              stitchTiles="stitch"
              result="noise"
            />
            <feColorMatrix
              type="matrix"
              values="0 0 0 0 0
                      0 0 0 0 0
                      0 0 0 0 0
                      0 0 0 1 0"
              in="noise"
              result="monoNoise"
            />
          </filter>
        </defs>
        <rect
          width="100%"
          height="100%"
          filter={`url(#${filterId}-light)`}
        />
      </svg>

      {/* Dark mode - SVG procedural noise */}
      <svg
        className={`absolute inset-0 hidden h-full w-full opacity-[0.10] dark:block ${className ?? ''}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <filter
            id={filterId}
            x="0%"
            y="0%"
            width="100%"
            height="100%"
          >
            <feTurbulence
              type="fractalNoise"
              baseFrequency={scale}
              numOctaves={4}
              stitchTiles="stitch"
            />
          </filter>
        </defs>
        <rect
          width="100%"
          height="100%"
          filter={`url(#${filterId})`}
          style={{ mixBlendMode: 'soft-light' }}
        />
      </svg>
    </>
  )
}
